// dashboard/components/CoursesInProgress.jsx

import React, { useRef, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  Avatar,
} from "@mui/material";

import {
  PlayCircle,
  School,
  AccessTime,
  VideoLibrary,
  MenuBook,
  PlayArrow,
  Description,
  ChevronLeft,
  ChevronRight,
  People,
  ArrowForward,
} from "@mui/icons-material";

// ============================================================
// COURSE DATA
// ============================================================

const COURSES = [
  {
    id: 1,
    title: "EUR/USD Mastery",
    instructor: "John Smith",
    type: "video",
    duration: "12 hours",
    lessons: 24,
    progress: 75,
    color: "#4a6cf7",
    status: "In Progress",
    members: 1234,

    // Replace this with your actual video thumbnail
    thumbnail:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=900&q=85",
  },

  {
    id: 2,
    title: "Forex Trading Psychology",
    instructor: "Sarah Johnson",
    type: "book",
    pages: 320,
    chapters: 12,
    progress: 45,
    color: "#f5a623",
    status: "In Progress",
    members: 876,

    // Book cover
    thumbnail:
      "https://images.unsplash.com/photo-1543002588-bfa74002ed7e?auto=format&fit=crop&w=900&q=85",
  },

  {
    id: 3,
    title: "GBP/USD Strategies",
    instructor: "Michael Chen",
    type: "video",
    duration: "8 hours",
    lessons: 18,
    progress: 90,
    color: "#2ecc71",
    status: "Almost Done",
    members: 2345,

    thumbnail:
      "https://images.unsplash.com/photo-1559526324-593bc073d938?auto=format&fit=crop&w=900&q=85",
  },

  {
    id: 4,
    title: "USD/JPY Trading Guide",
    instructor: "Emily Davis",
    type: "book",
    pages: 280,
    chapters: 10,
    progress: 30,
    color: "#e74c3c",
    status: "Just Started",
    members: 567,

    thumbnail:
      "https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=900&q=85",
  },

  {
    id: 5,
    title: "Forex Scalping Techniques",
    instructor: "David Wilson",
    type: "video",
    duration: "6 hours",
    lessons: 15,
    progress: 60,
    color: "#9b59b6",
    status: "In Progress",
    members: 987,

    thumbnail:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=900&q=85",
  },

  {
    id: 6,
    title: "AUD/USD Fundamentals",
    instructor: "Lisa Anderson",
    type: "book",
    pages: 350,
    chapters: 14,
    progress: 20,
    color: "#1abc9c",
    status: "Just Started",
    members: 432,

    thumbnail:
      "https://images.unsplash.com/photo-1495446815901-a7297e633e8d?auto=format&fit=crop&w=900&q=85",
  },
];

// ============================================================
// STATUS COLORS
// ============================================================

const getStatusColor = (status) => {
  switch (status) {
    case "In Progress":
      return "#4a6cf7";

    case "Almost Done":
      return "#2ecc71";

    case "Just Started":
      return "#f5a623";

    case "Completed":
      return "#2ecc71";

    default:
      return "#4a6cf7";
  }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const CoursesInProgress = () => {
  const carouselRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // ==========================================================
  // CHECK CAROUSEL POSITION
  // ==========================================================

  const updateScrollButtons = () => {
    const container = carouselRef.current;

    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(container.scrollLeft > 5);

    setCanScrollRight(container.scrollLeft < maxScrollLeft - 5);
  };

  // ==========================================================
  // SCROLL CAROUSEL
  // ==========================================================

  const scrollCarousel = (direction) => {
    const container = carouselRef.current;

    if (!container) return;

    const cardWidth = 390;

    container.scrollBy({
      left: direction === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });

    setTimeout(updateScrollButtons, 400);
  };

  // ==========================================================
  // MEDIA ICON
  // ==========================================================

  const getMediaIcon = (type) => {
    if (type === "video") {
      return <VideoLibrary sx={{ fontSize: 15 }} />;
    }

    return <MenuBook sx={{ fontSize: 15 }} />;
  };

  // ==========================================================
  // MEDIA LABEL
  // ==========================================================

  const getMediaLabel = (type) => {
    return type === "video" ? "Video Course" : "Book Course";
  };

  // ==========================================================
  // BUTTON TEXT
  // ==========================================================

  const getButtonText = (course) => {
    if (course.progress === 0) {
      return course.type === "video" ? "Start Course" : "Start Reading";
    }

    if (course.progress === 100) {
      return "Review Course";
    }

    return course.type === "video" ? "Continue Watching" : "Continue Reading";
  };

  // ==========================================================
  // HANDLE COURSE CLICK
  // ==========================================================

  const handleCourseClick = (course) => {
    console.log("Opening course:", course);

    // Later you can navigate to:
    //
    // navigate(`/courses/${course.id}`)
    //
    // or:
    //
    // navigate("/course-details", {
    //   state: { course }
    // });
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* =====================================================
          SECTION HEADER
      ====================================================== */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        {/* LEFT SIDE */}
        <Box sx={{ textAlign: "left" }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1a1a2e",
              fontSize: {
                xs: "1.2rem",
                sm: "1.35rem",
                md: "1.5rem",
              },
            }}
          >
            Courses In Progress
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#8a8a9a",
              mt: 0.4,
            }}
          >
            Continue your Forex learning journey
          </Typography>
        </Box>

        {/* RIGHT SIDE */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* VIEW ALL */}

          <Button
            endIcon={<ArrowForward />}
            sx={{
              color: "#4a6cf7",
              fontWeight: 600,
              textTransform: "none",
              display: {
                xs: "none",
                sm: "flex",
              },

              "&:hover": {
                backgroundColor: "rgba(74,108,247,0.08)",
              },
            }}
          >
            View All
          </Button>

          {/* LEFT ARROW */}

          <IconButton
            onClick={() => scrollCarousel("left")}
            disabled={!canScrollLeft}
            sx={{
              width: 42,
              height: 42,
              border: "1px solid #e5e7eb",
              backgroundColor: "#fff",

              boxShadow: "0 3px 12px rgba(0,0,0,0.06)",

              "&:hover": {
                backgroundColor: "#f7f8fa",
              },

              "&.Mui-disabled": {
                opacity: 0.35,
              },
            }}
          >
            <ChevronLeft />
          </IconButton>

          {/* RIGHT ARROW */}

          <IconButton
            onClick={() => scrollCarousel("right")}
            disabled={!canScrollRight}
            sx={{
              width: 42,
              height: 42,
              border: "1px solid #e5e7eb",
              backgroundColor: "#fff",

              boxShadow: "0 3px 12px rgba(0,0,0,0.06)",

              "&:hover": {
                backgroundColor: "#f7f8fa",
              },

              "&.Mui-disabled": {
                opacity: 0.35,
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {/* =====================================================
          CAROUSEL
      ====================================================== */}

      <Box
        ref={carouselRef}
        onScroll={updateScrollButtons}
        sx={{
          display: "flex",

          gap: {
            xs: 2,
            md: 2.5,
          },

          overflowX: "auto",

          scrollBehavior: "smooth",

          scrollSnapType: "x mandatory",

          pb: 2,

          px: {
            xs: 0.5,
            md: 0.5,
          },

          /* Hide scrollbar */

          scrollbarWidth: "none",

          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {COURSES.map((course) => (
          <Card
            key={course.id}
            sx={{
              flex: {
                xs: "0 0 86%",
                sm: "0 0 48%",
                md: "0 0 31.5%",
              },

              minWidth: {
                xs: 280,
                sm: 300,
                md: 340,
              },

              borderRadius: "16px",

              border: "1px solid #e5e7eb",

              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",

              overflow: "hidden",

              scrollSnapAlign: "start",

              backgroundColor: "#fff",

              transition: "all 0.25s ease",

              "&:hover": {
                transform: "translateY(-4px)",

                boxShadow: "0 12px 30px rgba(0,0,0,0.10)",
              },
            }}
          >
            {/* =================================================
                THUMBNAIL
            ================================================== */}

            <Box
              sx={{
                position: "relative",

                height: {
                  xs: 175,
                  sm: 185,
                  md: 195,
                },

                overflow: "hidden",

                backgroundColor: course.color,
              }}
            >
              {/* IMAGE */}

              <Box
                component="img"
                src={course.thumbnail}
                alt={course.title}
                sx={{
                  width: "100%",
                  height: "100%",

                  objectFit: "cover",

                  display: "block",

                  transition: "transform 0.4s ease",

                  "&:hover": {
                    transform: "scale(1.04)",
                  },
                }}
              />

              {/* DARK OVERLAY */}

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,

                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.55) 100%)",
                }}
              />

              {/* =================================================
                  COURSE TYPE BADGE
              ================================================== */}

              <Chip
                icon={getMediaIcon(course.type)}
                label={getMediaLabel(course.type)}
                size="small"
                sx={{
                  position: "absolute",

                  top: 12,
                  right: 12,

                  height: 28,

                  backgroundColor: "rgba(255,255,255,0.94)",

                  color: course.type === "video" ? "#e74c3c" : "#3498db",

                  fontWeight: 700,

                  fontSize: "11px",

                  backdropFilter: "blur(8px)",

                  "& .MuiChip-icon": {
                    color: course.type === "video" ? "#e74c3c" : "#3498db",
                  },
                }}
              />

              {/* =================================================
                  PLAY BUTTON FOR VIDEO
              ================================================== */}

              {course.type === "video" && (
                <IconButton
                  onClick={() => handleCourseClick(course)}
                  sx={{
                    position: "absolute",

                    left: "50%",
                    top: "50%",

                    transform: "translate(-50%, -50%)",

                    width: 58,
                    height: 58,

                    backgroundColor: "rgba(255,255,255,0.95)",

                    color: "#1a1a2e",

                    boxShadow: "0 5px 20px rgba(0,0,0,0.20)",

                    "&:hover": {
                      backgroundColor: "#fff",

                      transform: "translate(-50%, -50%) scale(1.08)",
                    },
                  }}
                >
                  <PlayArrow
                    sx={{
                      fontSize: 32,
                      ml: 0.3,
                    }}
                  />
                </IconButton>
              )}

              {/* =================================================
                  BOOK ICON
              ================================================== */}

              {course.type === "book" && (
                <Box
                  sx={{
                    position: "absolute",

                    left: "50%",
                    top: "50%",

                    transform: "translate(-50%, -50%)",

                    width: 58,
                    height: 58,

                    borderRadius: "50%",

                    backgroundColor: "rgba(255,255,255,0.95)",

                    display: "flex",

                    alignItems: "center",
                    justifyContent: "center",

                    boxShadow: "0 5px 20px rgba(0,0,0,0.20)",
                  }}
                >
                  <MenuBook
                    sx={{
                      fontSize: 30,
                      color: course.color,
                    }}
                  />
                </Box>
              )}

              {/* =================================================
                  PROGRESS OVER IMAGE
              ================================================== */}

              <Box
                sx={{
                  position: "absolute",

                  bottom: 0,
                  left: 0,
                  right: 0,

                  height: 4,

                  backgroundColor: "rgba(255,255,255,0.35)",
                }}
              >
                <Box
                  sx={{
                    height: "100%",

                    width: `${course.progress}%`,

                    backgroundColor: course.color,

                    transition: "width 0.5s ease",
                  }}
                />
              </Box>
            </Box>

            {/* =================================================
                CARD CONTENT
            ================================================== */}

            <CardContent
              sx={{
                p: {
                  xs: 2,
                  md: 2.3,
                },
              }}
            >
              {/* TITLE */}

              <Typography
                sx={{
                  fontWeight: 700,

                  color: "#1a1a2e",

                  fontSize: {
                    xs: "1rem",
                    md: "1.05rem",
                  },

                  lineHeight: 1.3,

                  mb: 0.5,

                  display: "-webkit-box",

                  WebkitLineClamp: 2,

                  WebkitBoxOrient: "vertical",

                  overflow: "hidden",

                  minHeight: "2.7em",
                }}
              >
                {course.title}
              </Typography>

              {/* INSTRUCTOR */}

              <Typography
                variant="body2"
                sx={{
                  color: "#8a8a9a",

                  mb: 1.8,
                }}
              >
                {course.instructor}
              </Typography>

              {/* =================================================
                  DETAILS
              ================================================== */}

              <Box
                sx={{
                  display: "flex",

                  alignItems: "center",

                  justifyContent: "space-between",

                  mb: 1.5,
                }}
              >
                {/* DURATION / PAGES */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <AccessTime
                    sx={{
                      fontSize: 15,
                      color: "#8a8a9a",
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#8a8a9a",
                    }}
                  >
                    {course.type === "video"
                      ? course.duration
                      : `${course.pages} pages`}
                  </Typography>
                </Box>

                {/* STUDENTS */}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <People
                    sx={{
                      fontSize: 15,
                      color: "#8a8a9a",
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: "#8a8a9a",
                    }}
                  >
                    {course.members.toLocaleString()} students
                  </Typography>
                </Box>
              </Box>

              {/* =================================================
                  STATUS + PERCENTAGE
              ================================================== */}

              <Box
                sx={{
                  display: "flex",

                  alignItems: "center",

                  justifyContent: "space-between",

                  mb: 0.8,
                }}
              >
                <Chip
                  label={course.status}
                  size="small"
                  sx={{
                    height: 24,

                    backgroundColor: `${getStatusColor(course.status)}15`,

                    color: getStatusColor(course.status),

                    fontWeight: 700,

                    fontSize: "10px",
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a2e",
                  }}
                >
                  {course.progress}%
                </Typography>
              </Box>

              {/* =================================================
                  PROGRESS BAR
              ================================================== */}

              <LinearProgress
                variant="determinate"
                value={course.progress}
                sx={{
                  height: 5,

                  borderRadius: 5,

                  backgroundColor: "#eef2f7",

                  "& .MuiLinearProgress-bar": {
                    backgroundColor: course.color,

                    borderRadius: 5,
                  },
                }}
              />

              {/* =================================================
                  LESSON / CHAPTER INFO
              ================================================== */}

              <Box
                sx={{
                  display: "flex",

                  justifyContent: "space-between",

                  mt: 1.3,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#8a8a9a",
                  }}
                >
                  {course.type === "video"
                    ? `${course.lessons} lessons`
                    : `${course.chapters} chapters`}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: course.color,
                    fontWeight: 600,
                  }}
                >
                  {course.type === "video" ? "Watch" : "Read"}
                </Typography>
              </Box>

              {/* =================================================
                  CONTINUE BUTTON
              ================================================== */}

              <Button
                variant="contained"
                fullWidth
                startIcon={
                  course.type === "video" ? <PlayArrow /> : <Description />
                }
                onClick={() => handleCourseClick(course)}
                sx={{
                  mt: 1.8,

                  borderRadius: 2,

                  backgroundColor: "#1a1a2e",

                  textTransform: "none",

                  fontWeight: 600,

                  py: 1,

                  "&:hover": {
                    backgroundColor: "#2a2a4e",
                  },
                }}
              >
                {getButtonText(course)}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* =====================================================
          MOBILE VIEW ALL
      ====================================================== */}

      <Box
        sx={{
          display: {
            xs: "flex",
            sm: "none",
          },

          justifyContent: "center",

          mt: 1,
        }}
      >
        <Button
          endIcon={<ArrowForward />}
          sx={{
            color: "#4a6cf7",
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          View All Courses
        </Button>
      </Box>
    </Box>
  );
};

export default CoursesInProgress;
